import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Popover } from 'radix-ui';
import { Ban, Bell, Radar, ClipboardList, FileWarning, HandCoins, MessageSquare, PackageCheck, PackageX, ReceiptText, type LucideIcon } from 'lucide-react';
import { useAgency } from '@/auth/AgencyProvider';
import { ActivityList } from '@/components/activity/ActivityList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useActivity } from '@/data/useActivity';
import { useActivityNotify } from '@/data/useActivityPrefs';
import { useNotifications } from '@/data/useNotifications';
import { useTeam } from '@/data/useTeam';
import type { Task } from '@/data/types';
import { badgeText } from '@/lib/chat';
import { dateTimeInZone } from '@/lib/dates';
import { notificationHref, type Notification, type NotificationKind } from '@/lib/notifications';
import { taskTitle, type TaskTitleClock, isPodMissingDetail } from '@/lib/taskTitle';
import { cn } from '@/lib/utils';

/**
 * The bell (change spec 2026-09-24/topbar, design 2.5). The badge is the feed's unread count for the
 * current agency. Opening the panel marks everything up to the newest notice read (the badge clears at
 * once); the notices that were unread keep their dot while the panel stays open, so the office still
 * sees what was new. Each notice is a link to where the office acts on it.
 *
 * activity-feed (design 9.3): with `activity.read` the panel has two tabs, "For me" (the notices above,
 * unchanged) and "Activity" (what the team did), each with its own count; the badge is the sum. Opening
 * the panel or switching tabs marks read only the tab shown. The last tab is remembered per browser.
 * Without the permission the bell is exactly the one above: no tabs, no activity read.
 *
 * activity-master-switch: the Activity tab carries a Turn off / Turn on shortcut for the master switch.
 * While off it shows no counter and no unread dot, and the badge is For me only; the history still lists.
 */

type BellTab = 'forMe' | 'activity';
const TAB_KEY = 'coreway.bell.tab';

function storedTab(): BellTab {
  try {
    return localStorage.getItem(TAB_KEY) === 'activity' ? 'activity' : 'forMe';
  } catch {
    return 'forMe';
  }
}

function storeTab(tab: BellTab): void {
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // storage unavailable: the tab is simply not remembered
  }
}

const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  chat_message: MessageSquare,
  load_rejected: PackageX,
  task_assigned: ClipboardList,
  document_expiring: FileWarning,
  invoice_overdue: ReceiptText,
  load_delivered: PackageCheck,
  pod_received: PackageCheck,
  receivable_overdue: HandCoins,
  load_canceled: Ban,
  load_board_refused: Radar,
};

const TASK_SOURCE: Partial<Record<NotificationKind, Task['source']>> = {
  load_rejected: 'load_rejected',
  document_expiring: 'document_expiry',
  invoice_overdue: 'invoice_overdue',
  receivable_overdue: 'receivable_overdue',
  load_canceled: 'load_canceled',
  task_assigned: 'manual',
};

type T = ReturnType<typeof useTranslation>['t'];

/** The notice's line, in the UI language. Task notices reuse the Upcoming Tasks copy (taskTitle). */
export function notificationText(n: Notification, t: T, clock: TaskTitleClock = {}): string {
  switch (n.kind) {
    case 'chat_message':
      return t('notifications.chat', { driver: n.driverName ?? t('notifications.aDriver'), count: Number(n.detail ?? 1) });
    case 'load_delivered':
      return t('notifications.delivered', { load: n.loadNumber ?? '' });
    case 'pod_received':
      return t('notifications.pod', { load: n.loadNumber ?? '' });
    // loadboard-carrier-quota (0377): the feed's detail is the carrier's name (DBA, else legal name).
    case 'load_board_refused':
      return t('notifications.loadBoardRefused', { carrier: n.detail ?? t('notifications.aCarrier') });
    default: {
      const source = n.kind === 'task_assigned' && isPodMissingDetail(n.detail) ? 'pod_missing' : (TASK_SOURCE[n.kind] ?? 'manual');
      return taskTitle({ source, title: n.title ?? '', detail: n.detail, loadId: n.loadId }, n.loadNumber, t, clock);
    }
  }
}

export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const agency = useAgency();
  const timeZone = agency.status === 'ready' ? agency.agency.timeZone : 'America/Chicago';
  const feed = useNotifications();
  const activity = useActivity();
  const master = useActivityNotify(activity.enabled);
  const activityOn = activity.enabled && master.notify;
  const team = useTeam();
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<BellTab>(storedTab);
  // The rows that were unread when their tab was shown: they keep their dot until the panel closes.
  const [wasUnread, setWasUnread] = React.useState<Set<string>>(new Set());
  const [activityWasUnread, setActivityWasUnread] = React.useState<Set<string>>(new Set());
  const activityUnread = activityOn ? activity.unreadCount : 0;
  const total = feed.unreadCount + activityUnread;
  const badge = badgeText(total);
  const shown: BellTab = activity.enabled ? tab : 'forMe';

  // A flip (here or in Settings) re-reads the Activity count: the server answers 0 while off.
  const { reload: reloadActivity } = activity;
  const firstNotify = React.useRef(true);
  React.useEffect(() => {
    if (firstNotify.current) {
      firstNotify.current = false;
      return;
    }
    reloadActivity();
  }, [master.notify, reloadActivity]);

  // The target is the value the button offers (its label), sent as is; never a toggle of the hook's state.
  const setMaster = async (on: boolean) => {
    const res = await master.set(on);
    if (res.error) console.warn('NotificationsBell: activity_notify_set failed', res.error);
  };
  const masterTarget = !master.notify;

  const roleOf = React.useCallback((id: string) => team.members.find((m) => m.userId === id)?.role ?? null, [team.members]);

  const markShown = (which: BellTab) => {
    if (which === 'forMe') {
      setWasUnread(new Set(feed.items.filter((n) => n.unread).map((n) => n.id)));
      const newest = feed.items[0];
      if (newest && feed.unreadCount > 0) void feed.markRead(newest.at);
    } else {
      setActivityWasUnread(new Set(activityOn ? activity.items.filter((n) => n.unread).map((n) => n.id) : []));
      const newest = activity.items[0];
      if (newest && activityUnread > 0) void activity.markRead(newest.at);
    }
  };

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setWasUnread(new Set());
      setActivityWasUnread(new Set());
      return;
    }
    markShown(shown);
  };

  const onTabChange = (value: string) => {
    const next: BellTab = value === 'activity' ? 'activity' : 'forMe';
    setTab(next);
    storeTab(next);
    markShown(next);
  };

  const forMeList =
    feed.status === 'error' && feed.items.length === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('notifications.error')}</p>
    ) : feed.status === 'loading' && feed.items.length === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('common.loading')}</p>
    ) : feed.items.length === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('notifications.empty')}</p>
    ) : (
      <ul className="divide-y divide-hairline">
        {feed.items.map((n) => {
          const Icon = KIND_ICON[n.kind];
          const unread = n.unread || wasUnread.has(n.id);
          return (
            <li key={n.id}>
              <Link
                to={notificationHref(n)}
                onClick={() => setOpen(false)}
                className={cn('flex gap-3 px-4 py-3 hover:bg-secondary', unread && 'bg-accent-wash')}
              >
                <Icon className="mt-0.5 size-5 shrink-0 text-ink-muted" aria-hidden="true" />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className={cn('text-sm text-ink', unread && 'font-semibold')}>{notificationText(n, t, { timeZone: agency.status === 'ready' ? agency.agency.timeZone : undefined, locale: i18n.resolvedLanguage === 'es' ? 'es-US' : 'en-US' })}</span>
                  <span className="text-xs text-ink-muted">
                    {t(`notifications.kinds.${n.kind}`)} · {dateTimeInZone(n.at, timeZone, i18n.resolvedLanguage ?? 'en')}
                  </span>
                </span>
                {unread && (
                  <span
                    data-testid="notification-unread"
                    role="img"
                    className="mt-1.5 size-2 shrink-0 rounded-full bg-accent-base"
                    aria-label={t('notifications.unread')}
                  />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    );

  const activityList =
    activity.status === 'error' && activity.items.length === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('notifications.activityError')}</p>
    ) : activity.status === 'loading' && activity.items.length === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('common.loading')}</p>
    ) : activity.items.length === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('notifications.activityEmpty')}</p>
    ) : (
      <ActivityList
        items={activityOn ? activity.items : activity.items.map((n) => ({ ...n, unread: false }))}
        wasUnread={activityWasUnread}
        timeZone={timeZone}
        roleOf={roleOf}
        onNavigate={() => setOpen(false)}
      />
    );

  const count = (n: number) =>
    n > 0 ? (
      <span
        className="tabular-figures grid min-w-5 place-items-center rounded-full bg-accent-wash px-1.5 text-xs font-semibold text-ink"
        aria-label={t('notifications.tabCount', { count: n })}
      >
        {badgeText(n)}
      </span>
    ) : null;

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={total > 0 ? t('topbar.notificationCount', { count: total }) : t('topbar.notifications')}
          className="relative grid size-touch shrink-0 place-items-center rounded-md text-ink-secondary hover:bg-secondary"
        >
          <Bell className="size-5" aria-hidden="true" />
          {badge && (
            <span className="tabular-figures absolute top-1 right-1 grid min-w-5 place-items-center rounded-full bg-destructive px-1 text-xs font-semibold text-destructive-foreground">
              {badge}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={16}
          aria-label={t('notifications.title')}
          className="z-50 flex max-h-[min(32rem,calc(100dvh-6rem))] w-[min(24rem,calc(100vw-2rem))] flex-col rounded-lg border border-hairline bg-popover text-popover-foreground shadow-overlay"
        >
          <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
            <h2 className="text-base font-semibold text-ink">{t('notifications.title')}</h2>
          </div>
          {activity.enabled ? (
            <Tabs value={shown} onValueChange={onTabChange} className="min-h-0 flex-1 gap-0">
              <TabsList className="w-full shrink-0 px-2">
                <TabsTrigger value="forMe" className="text-sm">
                  {t('notifications.tabs.forMe')}
                  {count(feed.unreadCount)}
                </TabsTrigger>
                <TabsTrigger value="activity" className="text-sm">
                  {t('notifications.tabs.activity')}
                  {count(activityUnread)}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="forMe" className="relative min-h-0 overflow-y-auto">
                {forMeList}
              </TabsContent>
              <TabsContent value="activity" className="relative flex min-h-0 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto">{activityList}</div>
                {!master.notify && (
                  <p className="shrink-0 border-t border-hairline px-4 py-2 text-sm text-ink-muted">{t('notifications.activityOff')}</p>
                )}
                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-hairline px-4 py-2">
                  <Link to="/activity" onClick={() => setOpen(false)} className="text-sm font-medium text-accent-base hover:underline">
                    {t('notifications.viewAllActivity')}
                  </Link>
                  <button
                    type="button"
                    onClick={() => void setMaster(masterTarget)}
                    disabled={!master.ready}
                    aria-label={t(master.notify ? 'notifications.activityTurnOffLabel' : 'notifications.activityTurnOnLabel')}
                    className="rounded-md px-2 py-1 text-sm font-medium text-ink-secondary hover:bg-secondary disabled:opacity-50"
                  >
                    {t(master.notify ? 'notifications.activityTurnOff' : 'notifications.activityTurnOn')}
                  </button>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="relative min-h-0 flex-1 overflow-y-auto">{forMeList}</div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
